import { Component, EventEmitter, Input, Output } from "@angular/core";
import { FormGroup, ReactiveFormsModule } from "@angular/forms";
import { ApplyCreditCard } from "../../../../core/models/orders/applyCreditCard";
import { CustomerPaymentMethodDto } from "../../../../core/models/orders/customerPaymentMethodDto";
import { ClientSearchList } from "../../../../core/models/shared/clientSearchList";
import { AddClientContact } from "../../../../core/models/client/addClientContact";
import { CommonModule } from "@angular/common";
import { AddContactInformationComponent } from "../../../clients/components/add-contact-information/add-contact-information.component";
import { ShippingMethod } from "../../../../core/models/shared/shippingMethod";
import { Currency } from "../../../../core/models/shared/currencyList";
import { SelectChangeEvent, SelectModule } from "primeng/select";
import { InputTextModule } from "primeng/inputtext";
import { CheckboxChangeEvent, CheckboxModule } from "primeng/checkbox";
import { ClientAddressCustomize } from "../../../../core/models/client/details/clientAddresscustomize";
import { OrderLineItemDetailService } from "../../../../core/services/order-line-item-detail.service";

@Component({
  selector: "jni-header-details",
  templateUrl: "./header-details.component.html",
  imports: [
    CommonModule,
    AddContactInformationComponent,
    ReactiveFormsModule,
    SelectModule,
    InputTextModule,
    CheckboxModule,
  ],
  styleUrls: ["./header-details.component.scss"],
})
export class HeaderDetailsComponent {
  @Input() parent!: FormGroup;
  @Input() shippingProductsList: ShippingMethod[] = [];
  @Input() currencyList: Currency[] = [];
  @Input() formErrors!: Record<string, string>;
  @Input() customerPaymentMethodList: CustomerPaymentMethodDto[] = [];
  @Input() applyCreditCardList: ApplyCreditCard[] = [];
  @Input() contactLists: ClientAddressCustomize[] = [];
  @Input() customerBillToObject!: ClientSearchList;
  @Input() isCustomerPoRequiredField = false;
  @Output() validationErrorEvent = new EventEmitter();
  @Output() methodPaymentErrorEvent = new EventEmitter();
  @Output() contactListRefreshGrid = new EventEmitter();

  displayAddContact = false;
  addClientContact: AddClientContact = {} as AddClientContact;
  customerAccountNumber = "";
  partyNumber = "";
  applyCreditCardStatus = true;

  constructor(
    private _orderLineItemDetailService: OrderLineItemDetailService
  ) {}

  logValidationErrors() {
    this.validationErrorEvent.emit();
  }

  onCurrencyStatus(event: CheckboxChangeEvent) {
    if (event.checked) {
      this.parent.get(["orderDetails", "foreignCurrency"])?.enable();
      this.parent.patchValue({
        orderDetails: {
          foreignCurrencyStatus: true,
        },
      });
    } else {
      this.parent.get(["orderDetails", "foreignCurrency"])?.disable();
      this.parent.patchValue({
        orderDetails: {
          foreignCurrency: "USD",
        },
      });
    }
  }

  onShippingMethodChange(shippingMethodCode: SelectChangeEvent) {
    const target = shippingMethodCode.value;
    const index = this.shippingProductsList.findIndex(
      x => x.modeCodeD365 == target
    );

    if (index > -1) {
      this.patchValueInShippingMethod(
        this.shippingProductsList[index].modeDescriptionD365
      );
      const selectedShippingMethod = this.shippingProductsList[index];
      if (
        selectedShippingMethod.useFreightAccountD365?.toLowerCase() == "yes"
      ) {
        this.patchValueInAccount(selectedShippingMethod.jniFreightAcctNumD365);
      } else {
        this.patchValueInAccount("");
      }
    } else {
      this.patchValueInShippingMethod("");
    }
  }

  patchValueInShippingMethod(descriptionName: string) {
    this.parent.patchValue({
      orderDetails: {
        shippingMethodName: descriptionName,
      },
    });
  }

  onClearShippingMethodSelection() {
    this.patchValueInAccount("");
  }

  patchValueInAccount(name: string) {
    this.parent.patchValue({
      orderDetails: {
        account: name,
      },
    });
  }

  getCreditCard(event: string) {
    if (event === "CC") {
      this.applyCreditCardStatus = false;
    } else {
      this.applyCreditCardStatus = true;
    }
  }

  // applyBuyerLengthValidation(event: any) {
  //   if (event.target.value) {
  //     this.parent.patchValue({
  //       orderDetails: {
  //         buyer: event.target.value.substring(0, 50),
  //       },
  //     });
  //   }
  // }
  applyCustomerPoLengthValidation(event: Event) {
    const target = event.target as HTMLInputElement;
    if (target.value) {
      this.parent.patchValue({
        orderDetails: {
          customerPONumber: target.value.substring(0, 40),
        },
      });
    }
  }

  // onContactList(contactdropdown: any) {
  //   contactdropdown.open();
  // }

  // onCustomerSelection(name: string, contactdropdown: any) {
  //   contactdropdown.close();
  //   this.parent.patchValue({
  //     orderDetails: {
  //       buyer: name,
  //     },
  //   });
  // }

  addContactInformation() {
    const getBuyerValue: string = this.parent.get([
      "orderDetails",
      "buyer",
    ])?.value;
    if (this.customerBillToObject?.clientAccount && getBuyerValue) {
      const getSplitValues = getBuyerValue.split(" ");
      this.customerAccountNumber = this.customerBillToObject.clientAccount;
      this.addClientContact.firstName = getSplitValues[0] || "";
      this.addClientContact.lastName = getSplitValues[1] || "";
      this.addClientContact.type = "Buyer";
    } else {
      this.customerAccountNumber = this.customerBillToObject.clientAccount;
      this.addClientContact.firstName = "";
      this.addClientContact.lastName = "";
      this.addClientContact.type = "Buyer";
    }
    this.displayAddContact = true;
  }

  isContactAdded(obj: boolean) {
    if (obj) {
      this.contactListRefreshGrid.emit(true);
    }
  }

  populateContactAddedInfo(obj: string) {
    if (obj) {
      this.parent.patchValue({
        orderDetails: {
          buyer: obj,
        },
      });
    }
  }

  patchValueInCurrency(value = "USD") {
    this.parent.patchValue({
      orderDetails: {
        foreignCurrency: value,
      },
    });
  }

  /**
   * Returns true when the currently selected shipping method is a
   * Nadel freight account (e.g. FedEx / UPS Nadel Account). In that case
   * the supplier account number must be auto-populated and non-editable.
   */
  get isNadelFreightAccount(): boolean {
    const shippingMethodCode = this.parent?.get([
      "orderDetails",
      "shippingMethod",
    ])?.value;
    if (!shippingMethodCode || !this.shippingProductsList?.length) {
      return false;
    }
    const method = this.shippingProductsList.find(
      x => x.modeCodeD365 === shippingMethodCode
    );

    if (method?.useFreightAccountD365?.toLowerCase() !== "yes") {
      return false;
    }

    // Client requirement: only hide/lock the account number when the
    // shipping method is UPS or FedEx (Nadel freight accounts).
    const description = method?.modeDescriptionD365?.toLowerCase() ?? "";
    return description.includes("ups") || description.includes("fedex");
  }
}
